import { APP_STORE_PATH, STARTUP_SAMPLES_PATH } from './appDataConventions';

/**
 * Files chef writes into a spec-driven prototype and the AI must not change: the data layer and
 * sign-in (chef `appDataLayer.ts`) and the theme (chef `themeTokenizer.ts`). Chef protects them on
 * the writes it executes; this protects them on the streaming path, where the browser executes
 * the AI's writeFile and searchReplace. Testing (22 Sep 2026): asked to "make appStore.ts log
 * every save", an edit turn changed it here, so that version shipped a modified data layer until
 * chef re-seeded the file on the next turn.
 *
 * Only prototypes that have `src/lib/appStore.ts` are spec-driven; in older prototypes the model
 * owns every file, including the theme, and nothing is refused.
 */
export const AUTH_CONTEXT_PATH = 'src/context/AuthContext.tsx';

const PLATFORM_FILES: Record<string, string> = {
  [APP_STORE_PATH]: 'Pages use useEntity and useAuth from it; they never change it.',
  [STARTUP_SAMPLES_PATH]: "It holds the startup's shared records for the preview.",
  [AUTH_CONTEXT_PATH]: 'Pages import useAuth and DEMO_ACCOUNTS from it.',
  'src/index.css': 'The theme comes from the startup design system.',
  'src/lib/design-system.ts': 'The theme comes from the startup design system.',
};

/**
 * The refusal the AI gets for writing `path`, or null when it may. A file the platform only
 * writes on some prototypes (AuthContext.tsx, when the spec has sign-in) is refused only once it
 * exists.
 */
export function platformFileRefusal(
  path: string,
  workspace: Readonly<Record<string, { content?: string } | undefined>>,
): string | null {
  const reason = PLATFORM_FILES[path];
  if (!reason || !workspace[APP_STORE_PATH]) return null;
  if (path === AUTH_CONTEXT_PATH && !workspace[AUTH_CONTEXT_PATH]) return null;
  return `Refused: ${path} is written by the platform and cannot be changed. ${reason}`;
}
